import {
  Content,
  Manifest,
  ManifestService,
  Page,
  Section
} from '@mobiz-sa/smart-renderer';
import { SectionType } from '@mobiz-sa/smart-renderer/dist/lib/renderer/enums';
import { Column } from '@mobiz-sa/smart-renderer/dist/lib/renderer/interfaces';
import {
  applyPatch as jsonPatchApply,
  compare as jsonPatchCompare,
  Operation
} from 'fast-json-patch';
import _ from 'lodash';
import Vue from 'vue';
import { Commit, Dispatch } from 'vuex';
import { createAxios } from '../lib/axiosFactory';
import {
  AddNewContentPayload,
  ConvertContentToNewSectionPayload,
  MoveContentPayload,
  SetSectionOrderPayload
} from '../lib/interfaces/manifest';
import { errorParser } from '../lib/response/errorParser';
import { uuid } from '../lib/utilities/uuid';

interface ManifestStore {
  testData: undefined | Record<string, unknown>;
  manifest: undefined | Manifest;
  manifestShadow: undefined | Manifest;
  history: {
    actions: Array<Action>;
    currentIndex: number;
  };
}

interface Action {
  id: string;
  elementId?: string;
  timestamp: number;
  undo: { name: string; patch: Operation[] };
  redo: { name: string; patch: Operation[] };
  metadata?: Record<string, any>;
}

function createAction(params: {
  beforeManifest: Manifest;
  afterManifest: Manifest;
  elementId: string;
  undoDescription: string;
  redoDescription: string;
  metadata?: Record<string, any>;
}): Action {
  const patch = jsonPatchCompare(params.beforeManifest, params.afterManifest);
  const reversePatch = jsonPatchCompare(
    params.afterManifest,
    params.beforeManifest
  );

  return {
    id: uuid(),
    timestamp: Date.now(),
    elementId: params.elementId,

    undo: {
      name: params.undoDescription,
      patch: reversePatch
    },

    redo: {
      name: params.redoDescription,
      patch: patch
    },
    metadata: params.metadata || {}
  };
}

function correctSectionOrder(manifest: Manifest, pageId: string): void {
  _.orderBy(
    Object.values(manifest.page[pageId].section),
    'order',
    'asc'
  ).forEach((section: Section, index) => {
    section.order = index + 1;
  });
}

// For continuous updating the same content (within the same second),
// do not create a new action in history, instead replace the last one.
function checkIfActionIsOnSameComponent(
  history: ManifestStore['history'],
  id: string,
  amountOfSeconds = 1000
): {
  isOnSameComponent: boolean;
  lastAction: Action | null;
} {
  const action = history.currentIndex
    ? history.actions[history.currentIndex - 1]
    : null;

  return {
    isOnSameComponent:
      action &&
      action.elementId === id &&
      Date.now() - action.timestamp < amountOfSeconds,
    lastAction: action
  };
}

export const state = (): ManifestStore => ({
  testData: undefined,
  manifest: undefined as Manifest | undefined,
  manifestShadow: undefined,
  history: {
    actions: [],
    currentIndex: 0
  }
});

export const getters = {
  getManifest(state: ManifestStore): Manifest {
    return state.manifest;
  },

  canUndo(state: ManifestStore) {
    return state.history.currentIndex > 0;
  },

  canRedo(state: ManifestStore) {
    return state.history.currentIndex < state.history.actions.length;
  },

  getMetaData(state: ManifestStore) {
    return (
      state.history.actions[state.history.currentIndex - 1]?.metadata || {}
    );
  },

  getPage(state: ManifestStore) {
    const pageId = Object.keys(state.manifest.page)[0];
    return state.manifest.page[pageId];
  }
};

export const mutations = {
  setManifest(state: ManifestStore, payload: { manifest: Manifest }): void {
    state.manifest = payload.manifest;
  },

  setTestData(
    state: ManifestStore,
    payload: { testData: Record<string, unknown> }
  ): void {
    state.testData = payload.testData;
  },

  setPageLogoLoader(state: ManifestStore, loader: { show: boolean }): void {
    state.manifest.settings.loader.show = loader.show;
  },

  setPageStyles(
    state: ManifestStore,
    payload: {
      pageStyles: Record<string, any>;
    }
  ): void {
    const pageId = Object.keys(state.manifest.page)[0];

    const { isOnSameComponent, lastAction } = checkIfActionIsOnSameComponent(
      state.history,
      pageId,
      2000
    );

    if (isOnSameComponent) {
      const currentManifest = _.cloneDeep(state.manifest);

      const previousManifest = jsonPatchApply(
        currentManifest,
        lastAction.undo.patch,
        false,
        false
      ).newDocument;

      Vue.set(state.manifest.page[pageId], 'styles', payload.pageStyles);

      const action = createAction({
        beforeManifest: previousManifest,
        afterManifest: state.manifest,
        elementId: pageId,
        undoDescription: `Update page styles.`,
        redoDescription: `Update page styles.`,
        metadata: {}
      });

      state.history.actions.splice(state.history.currentIndex - 1, 1, action);
      state.history.currentIndex = state.history.actions.length;
    } else {
      const previousManifest = _.cloneDeep(state.manifest);

      Vue.set(state.manifest.page[pageId], 'styles', payload.pageStyles);

      const action = createAction({
        beforeManifest: previousManifest,
        afterManifest: state.manifest,
        elementId: pageId,
        undoDescription: `Update page styles.`,
        redoDescription: `Update page styles.`,
        metadata: {}
      });

      state.history.actions.splice(state.history.currentIndex);
      state.history.actions.push(action);
      state.history.currentIndex++;
    }
  },

  setManifestShadow(
    state: ManifestStore,
    payload: { manifest: Manifest }
  ): void {
    state.manifestShadow = payload.manifest;
  },

  setSectionConditions(
    state: ManifestStore,
    payload: {
      conditions: Section['conditions'];
      sectionId: string;
    }
  ): void {
    const { isOnSameComponent, lastAction } = checkIfActionIsOnSameComponent(
      state.history,
      payload.sectionId,
      2000
    );
    if (isOnSameComponent) {
      const currentManifest = _.cloneDeep(state.manifest);
      const previousManifest = jsonPatchApply(
        currentManifest,
        lastAction.undo.patch,
        false,
        false
      ).newDocument;

      const newManifest = _.cloneDeep(state.manifest);
      const { conditions, sectionId } = payload;
      const section = ManifestService.getSectionBySectionId(
        newManifest,
        sectionId
      );
      section.conditions = conditions;
      state.manifest = newManifest;

      const action = createAction({
        beforeManifest: previousManifest,
        afterManifest: state.manifest,
        elementId: sectionId,
        undoDescription: `Update section condition`,
        redoDescription: `Update section condition`,
        metadata: null
      });

      state.history.actions.splice(state.history.currentIndex - 1, 1, action);
      state.history.currentIndex = state.history.actions.length;
    } else {
      const previousManifest = _.cloneDeep(state.manifest);

      const newManifest = _.cloneDeep(state.manifest);
      const { conditions, sectionId } = payload;
      const section = ManifestService.getSectionBySectionId(
        newManifest,
        sectionId
      );
      section.conditions = conditions;
      state.manifest = newManifest;

      const action = createAction({
        beforeManifest: previousManifest,
        afterManifest: state.manifest,
        elementId: sectionId,
        undoDescription: `Update section conditions.`,
        redoDescription: `Update section conditions.`,
        metadata: {}
      });

      state.history.actions.splice(state.history.currentIndex);
      state.history.actions.push(action);
      state.history.currentIndex++;
    }
  },

  setSectionStyles(
    state: ManifestStore,
    payload: {
      styles: Section['styles'];
      sectionId: string;
    }
  ): void {
    const { isOnSameComponent, lastAction } = checkIfActionIsOnSameComponent(
      state.history,
      payload.sectionId
    );
    if (isOnSameComponent) {
      const currentManifest = _.cloneDeep(state.manifest);
      const previousManifest = jsonPatchApply(
        currentManifest,
        lastAction.undo.patch,
        false,
        false
      ).newDocument;

      const newManifest = _.cloneDeep(state.manifest);
      const { styles, sectionId } = payload;
      const section = ManifestService.getSectionBySectionId(
        newManifest,
        sectionId
      );
      section.styles = styles;
      state.manifest = newManifest;

      const action = createAction({
        beforeManifest: previousManifest,
        afterManifest: state.manifest,
        elementId: sectionId,
        undoDescription: `Update section styles`,
        redoDescription: `Update section styles`,
        metadata: null
      });

      state.history.actions.splice(state.history.currentIndex - 1, 1, action);
      state.history.currentIndex = state.history.actions.length;
    } else {
      const previousManifest = _.cloneDeep(state.manifest);
      const newManifest = _.cloneDeep(state.manifest);
      const { styles, sectionId } = payload;
      const section = ManifestService.getSectionBySectionId(
        newManifest,
        sectionId
      );
      section.styles = styles;
      state.manifest = newManifest;

      const action = createAction({
        beforeManifest: previousManifest,
        afterManifest: state.manifest,
        elementId: sectionId,
        undoDescription: `Update section styles.`,
        redoDescription: `Update section styles.`,
        metadata: {}
      });

      state.history.actions.splice(state.history.currentIndex);
      state.history.actions.push(action);
      state.history.currentIndex++;
    }
  },

  setContent(
    state: ManifestStore,
    payload: { content: Content; metadata: Record<string, any> }
  ): void {
    const { isOnSameComponent, lastAction } = checkIfActionIsOnSameComponent(
      state.history,
      payload.content.id
    );

    if (isOnSameComponent) {
      const currentManifest = _.cloneDeep(state.manifest);
      const previousManifest = jsonPatchApply(
        currentManifest,
        lastAction.undo.patch,
        false,
        false
      ).newDocument;

      const column = ManifestService.getColumnByContentId(
        state.manifest,
        payload.content.id
      );
      column.content[payload.content.id] = payload.content;

      const action = createAction({
        beforeManifest: previousManifest,
        afterManifest: state.manifest,
        elementId: payload.content.id,
        undoDescription: `Update ${payload.content.type}`,
        redoDescription: `Update ${payload.content.type}`,
        metadata: payload.metadata
      });

      state.history.actions.splice(state.history.currentIndex - 1, 1, action);
      state.history.currentIndex = state.history.actions.length;
    } else {
      const previousManifest = _.cloneDeep(state.manifest);
      const column = ManifestService.getColumnByContentId(
        state.manifest,
        payload.content.id
      );
      column.content[payload.content.id] = payload.content;

      const action = createAction({
        beforeManifest: previousManifest,
        afterManifest: state.manifest,
        elementId: payload.content.id,
        undoDescription: `Update ${payload.content.type}`,
        redoDescription: `Update ${payload.content.type}`,
        metadata: payload.metadata
      });

      state.history.actions.splice(state.history.currentIndex);
      state.history.actions.push(action);
      state.history.currentIndex++;
    }
  },

  // TODO: There is an edge case where currently undo is not working.
  // Text-content is problematic as applying the patch triggers 'setContent' inadvertently.
  // This results in incorrect behavior.
  undo(state: ManifestStore) {
    if (state.history.currentIndex > 0) {
      state.history.currentIndex--;
      state.manifest = jsonPatchApply(
        state.manifest,
        state.history.actions[state.history.currentIndex].undo.patch,
        false,
        false
      ).newDocument;
    }
  },

  redo(state: ManifestStore) {
    if (state.history.currentIndex < state.history.actions.length) {
      state.manifest = jsonPatchApply(
        state.manifest,
        state.history.actions[state.history.currentIndex].redo.patch,
        false,
        false
      ).newDocument;

      state.history.currentIndex++;
    }
  },

  setHistory(state: ManifestStore, history: ManifestStore['history']) {
    state.history = history;
  }

  // To go to a particular point in time in history (forward or backward)
  // This works, but commented out because it's most likely not useful based on UI/UX.
  // playback(state: ManifestStore, { actionId }) {
  //   const toIndex = state.history.findIndex((x) => x.id === actionId);
  //   if (state.history.currentIndex > toIndex) {
  //     while (state.history.currentIndex > toIndex) {
  //       state.history.currentIndex--;
  //       state.history.actions[state.history.currentIndex].undo.action();
  //     }
  //   } else {
  //     while (state.history.currentIndex <= toIndex) {
  //       state.history.actions[state.history.currentIndex].redo.action();
  //       state.history.currentIndex++;
  //     }
  //   }
  // }
};

export const actions = {
  async loadTemplate(
    { commit },
    { id, endpoint }: { id: string; endpoint?: string }
  ): Promise<any> {
    try {
      const apiEndpoint: string = endpoint
        ? endpoint
        : '/backend/templates-show';

      const response = await createAxios().get(apiEndpoint, {
        params: {
          id,
          format: 'json'
        }
      });

      const { manifest, testData } = response.data.data.attributes;
      commit('setManifest', { manifest: manifest });
      commit('setTestData', { testData: testData });
      commit('setManifestShadow', { manifest: _.cloneDeep(manifest) });
      commit('setHistory', { actions: [], currentIndex: 0 });

      return { success: true, errors: {}, body: {} };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async updateTemplate(
    {
      commit,
      state
    }: {
      commit: Commit;
      state: ManifestStore;
    },
    { id, endpoint }: { id: string; endpoint?: string }
  ): Promise<any> {
    try {
      const patch = jsonPatchCompare(
        state.manifestShadow as Manifest,
        state.manifest as Manifest
      );

      const apiEndpoint: string = endpoint
        ? endpoint
        : '/backend/templates-update';

      const response = await createAxios().patch(
        apiEndpoint,
        {
          data: {
            type: 'templates',
            id: 'e75ceafc-8ebc-479b-b39d-e384fa92190a',
            attributes: {
              manifestPatch: patch,
              ...(!endpoint && { testData: state.testData })
            }
          }
        },
        {
          params: {
            id
          }
        }
      );

      commit('setManifestShadow', {
        manifest: jsonPatchApply(state.manifestShadow, patch, false, false)
          .newDocument
      });
      return { success: true, errors: {}, body: {} };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  addSection(
    {
      commit,
      dispatch,
      state
    }: { commit: Commit; dispatch: Dispatch; state: ManifestStore },
    payload: { contentType: SectionType; order?: number }
  ): Section {
    const { order, contentType } = payload;

    const newSection: Section = ManifestService.initializeNewSection(
      state.manifest,
      Object.keys(state.manifest.page)[0],
      contentType
    );
    newSection.order = _.isNumber(order) ? order : newSection.order;

    const previousManifest = _.cloneDeep(state.manifest);
    const newManifest = _.cloneDeep(state.manifest);
    const homePage = Object.values(newManifest.page)[0] as Page;

    if (homePage.section == null) {
      homePage.section = {};
    }

    Vue.set(homePage.section, newSection.id, newSection);

    commit('setManifest', { manifest: newManifest });

    /**
     * order would be a float number.
     * e.g.
     * - 1.5 means that it should be inserted between 1 & 2
     * - 2.5 means that it should be inserted between 2 & 3
     */
    if (_.isNumber(order)) {
      dispatch('setSectionOrder', {
        movedSectionId: newSection.id,
        orderedSectionIds: _(newManifest.page[homePage.id].section)
          .values()
          .orderBy('order')
          .map('id')
          .value()
      } as SetSectionOrderPayload);
      return newSection;
    }

    const action = createAction({
      beforeManifest: previousManifest,
      afterManifest: newManifest,
      elementId: newSection.id,
      undoDescription: `Remove section`,
      redoDescription: `Add section`
    });

    const newHistory = _.cloneDeep(state.history);
    newHistory.actions.splice(newHistory.currentIndex);
    newHistory.actions.push(action);
    newHistory.currentIndex++;
    commit('setHistory', newHistory);

    return newSection;
  },

  duplicateSection(
    { commit, state }: { commit: Commit; state: ManifestStore },
    payload: { sectionId: string }
  ): Section {
    const previousManifest = _.cloneDeep(state.manifest);
    const newManifest = _.cloneDeep(state.manifest);
    const page = Object.values(newManifest.page).shift();
    const pageSections = Object.values(page.section);
    const duplicatedSection: Section = ManifestService.initializeNewSection(
      newManifest,
      page.id,
      undefined,
      payload.sectionId
    );
    const sectionsAfterDuplicatedSection = _.filter(pageSections, function (
      section: Section
    ) {
      return section.order >= duplicatedSection.order;
    });

    sectionsAfterDuplicatedSection.forEach((sectionItem: Section) => {
      page.section[sectionItem.id].order += 1;
    });

    Vue.set(page.section, duplicatedSection.id, duplicatedSection);

    correctSectionOrder(newManifest, page.id);

    commit('setManifest', { manifest: newManifest });

    const action = createAction({
      beforeManifest: previousManifest,
      afterManifest: newManifest,
      elementId: duplicatedSection.id,
      undoDescription: `Remove section`,
      redoDescription: `Add section`
    });

    const newHistory = _.cloneDeep(state.history);
    newHistory.actions.splice(newHistory.currentIndex);
    newHistory.actions.push(action);
    newHistory.currentIndex++;
    commit('setHistory', newHistory);

    return duplicatedSection;
  },

  removeSection(
    { commit, state }: { commit: Commit; state: ManifestStore },
    payload: { sectionId: string }
  ): void {
    const previousManifest = _.cloneDeep(state.manifest);
    const newManifest = _.cloneDeep(state.manifest);
    const page = ManifestService.getPageBySectionId(
      newManifest,
      payload.sectionId
    );

    Vue.set(page.section, payload.sectionId, null);
    delete page.section[payload.sectionId];

    correctSectionOrder(newManifest, page.id);

    commit('setManifest', { manifest: newManifest });

    const action = createAction({
      beforeManifest: previousManifest,
      afterManifest: newManifest,
      elementId: payload.sectionId,
      undoDescription: `Remove section`,
      redoDescription: `Add section`
    });

    const newHistory = _.cloneDeep(state.history);
    newHistory.actions.splice(newHistory.currentIndex);
    newHistory.actions.push(action);
    newHistory.currentIndex++;
    commit('setHistory', newHistory);
  },

  removeContent(
    { commit, state }: { commit: Commit; state: ManifestStore },
    { contentId }: { contentId: string }
  ): void {
    let newManifest = _.cloneDeep(state.manifest);
    const previousManifest = _.cloneDeep(state.manifest);

    newManifest = ManifestService.deleteContentByContentId(
      newManifest,
      contentId
    );

    newManifest = ManifestService.clearEmptySections(newManifest);

    commit('setManifest', { manifest: newManifest });

    const action = createAction({
      beforeManifest: previousManifest,
      afterManifest: newManifest,
      elementId: contentId,
      undoDescription: `Remove content`,
      redoDescription: `Add content`
    });

    const newHistory = _.cloneDeep(state.history);
    newHistory.actions.splice(newHistory.currentIndex);
    newHistory.actions.push(action);
    newHistory.currentIndex++;
    commit('setHistory', newHistory);
  },

  duplicateContent(
    { commit, state }: { commit: Commit; state: ManifestStore },
    { contentId }: { contentId: string }
  ): Content {
    const previousManifest = _.cloneDeep(state.manifest);
    let newManifest = _.cloneDeep(state.manifest);

    const x = ManifestService.duplicateContentInSameSection(
      newManifest,
      contentId
    );
    newManifest = x.manifest;
    const newContent = x.content;

    commit('setManifest', { manifest: newManifest });

    const action = createAction({
      beforeManifest: previousManifest,
      afterManifest: newManifest,
      elementId: newContent.id,
      undoDescription: `Remove content`,
      redoDescription: `Add content`
    });

    const newHistory = _.cloneDeep(state.history);
    newHistory.actions.splice(newHistory.currentIndex);
    newHistory.actions.push(action);
    newHistory.currentIndex++;
    commit('setHistory', newHistory);

    return newContent;
  },

  setSectionOrder(
    { commit, state }: { commit: Commit; state: ManifestStore },
    payload: SetSectionOrderPayload
  ): void {
    const { movedSectionId, orderedSectionIds } = payload;
    const previousManifest = _.cloneDeep(state.manifest);
    const newManifest = _.cloneDeep(state.manifest);
    const pageId = ManifestService.getPageBySectionId(
      newManifest,
      movedSectionId
    ).id;

    const sections: Page['section'] = _.chain(newManifest.page)
      .values()
      .first()
      .get('section')
      .value();

    // validate provided section ids
    const existingSectionIds = Object.keys(sections);
    if (_.xor(orderedSectionIds, existingSectionIds).length > 0) {
      throw new Error(
        'Invalid ordered section ids = ' + JSON.stringify(orderedSectionIds)
      );
    }

    const orderedSections: Page['section'] = _.chain(sections)
      .toPairs()
      .orderBy(([sectionId]) => orderedSectionIds.indexOf(sectionId))
      .map(([sectionId, section], i) => {
        section.order = i + 1;
        return [sectionId, section];
      })
      .fromPairs()
      .value();

    newManifest.page[pageId].section = orderedSections;
    commit('setManifest', { manifest: newManifest });

    const action = createAction({
      beforeManifest: previousManifest,
      afterManifest: state.manifest,
      elementId: movedSectionId,
      undoDescription: `Update section ${movedSectionId}`,
      redoDescription: `Update section ${movedSectionId}`
    });

    const newHistory = _.cloneDeep(state.history);
    newHistory.actions.splice(newHistory.currentIndex);
    newHistory.actions.push(action);
    newHistory.currentIndex++;
    commit('setHistory', newHistory);
  },

  moveContent(
    { commit, state }: { commit: Commit; state: ManifestStore },
    payload: MoveContentPayload
  ): void {
    const { moveToColumnId, movedContentId, orderedContentIds } = payload;
    const previousManifest = _.cloneDeep(state.manifest);
    let newManifest = _.cloneDeep(state.manifest);

    const movedContent = _.clone(
      ManifestService.getContentByContentId(newManifest, movedContentId)
    );
    if (!movedContent) throw new Error(`Content ${movedContentId} not found`);

    // remove content from original position
    newManifest = ManifestService.deleteContentByContentId(
      newManifest,
      movedContentId
    ) as Manifest;

    const moveToColumn: Column = ManifestService.getColumnByColumnId(
      newManifest,
      moveToColumnId
    );

    // add moved content to target column
    moveToColumn.content[movedContentId] = movedContent;

    //validate content Ids
    if (
      _.xor(orderedContentIds, Object.keys(moveToColumn.content)).length > 0
    ) {
      throw new Error(
        `Invalid ordered content ids = ${JSON.stringify(orderedContentIds)}`
      );
    }

    // update order of moved content
    moveToColumn.content = _.chain(moveToColumn.content)
      .toPairs()
      .orderBy(([contentId]) => orderedContentIds.indexOf(contentId))
      .map(([contentId, content], i) => {
        content.order = i + 1;
        return [contentId, content];
      })
      .fromPairs()
      .value();

    newManifest = ManifestService.clearEmptySections(newManifest);

    commit('setManifest', { manifest: newManifest });

    const action = createAction({
      beforeManifest: previousManifest,
      afterManifest: state.manifest,
      elementId: movedContentId,
      undoDescription: `Update content ${movedContentId}`,
      redoDescription: `Update content ${movedContentId}`
    });

    const newHistory = _.cloneDeep(state.history);
    newHistory.actions.splice(newHistory.currentIndex);
    newHistory.actions.push(action);
    newHistory.currentIndex++;
    commit('setHistory', newHistory);
  },

  addNewContent(
    {
      commit,
      dispatch,
      state
    }: { commit: Commit; dispatch: Dispatch; state: ManifestStore },
    payload: AddNewContentPayload
  ): Content {
    const { order, contentType, addToColumnId } = payload;

    const newContent: Content = ManifestService.createNewContent(
      state.manifest,
      contentType
    );
    newContent.order = _.isNumber(order) ? order : newContent.order;

    const previousManifest = _.cloneDeep(state.manifest);
    const newManifest = _.cloneDeep(state.manifest);

    const addToColumn: Column = ManifestService.getColumnByColumnId(
      newManifest,
      addToColumnId
    );

    // patch order in column, just in case the order is not defined
    addToColumn.content = _.chain(addToColumn.content)
      .toPairs()
      .map(([contentId, content], i) => {
        content.order = i + 1;
        return [contentId, content];
      })
      .fromPairs()
      .value();

    addToColumn.content[newContent.id] = newContent;

    // handle ordering of content
    addToColumn.content = _.chain(addToColumn.content)
      .toPairs()
      .orderBy(([contentId, content]) => {
        return content.order;
      })
      .map(([contentId, content], i) => {
        content.order = i + 1;
        return [contentId, content];
      })
      .fromPairs()
      .value();

    commit('setManifest', { manifest: newManifest });

    const action = createAction({
      beforeManifest: previousManifest,
      afterManifest: newManifest,
      elementId: newContent.id,
      undoDescription: `Remove content`,
      redoDescription: `Add content`
    });

    const newHistory = _.cloneDeep(state.history);
    newHistory.actions.splice(newHistory.currentIndex);
    newHistory.actions.push(action);
    newHistory.currentIndex++;
    commit('setHistory', newHistory);

    return newContent;
  },

  convertContentToNewSection(
    {
      commit,
      dispatch,
      state
    }: { commit: Commit; dispatch: Dispatch; state: ManifestStore },
    payload: ConvertContentToNewSectionPayload
  ): Content {
    const previousManifest = _.cloneDeep(state.manifest);
    let newManifest = _.cloneDeep(state.manifest);

    const { sectionOrder, contentId } = payload;

    const page = ManifestService.getPageByContentId(newManifest, contentId);

    const content = ManifestService.getContentByContentId(
      newManifest,
      contentId
    );

    /**
     * remove content from it's original position
     */
    delete ManifestService.getColumnByContentId(newManifest, contentId).content[
      contentId
    ];

    /**
     * create new section
     */
    const newSection: Section = ManifestService.initializeNewSection(
      newManifest,
      Object.keys(newManifest.page)[0]
    );
    newSection.order = _.isNumber(sectionOrder)
      ? sectionOrder
      : newSection.order;

    if (!page.section) page.section = {};
    page.section[newSection.id] = newSection;

    /**
     * save content to new section
     */
    ManifestService.getColumnsBySectionId(
      newManifest,
      newSection.id
    )[0].content[contentId] = content;

    const orderedSections: Page['section'] = _.chain(page.section)
      .toPairs()
      .orderBy(([sectionId, section]) => section.order)
      .map(([sectionId, section], i) => {
        section.order = i + 1;
        return [sectionId, section];
      })
      .fromPairs()
      .value();
    page.section = orderedSections;

    newManifest = ManifestService.clearEmptySections(newManifest);

    commit('setManifest', { manifest: newManifest });

    const action = createAction({
      beforeManifest: previousManifest,
      afterManifest: newManifest,
      elementId: newSection.id,
      undoDescription: `Un-convert content to section`,
      redoDescription: `Convert content to section`
    });

    const newHistory = _.cloneDeep(state.history);
    newHistory.actions.splice(newHistory.currentIndex);
    newHistory.actions.push(action);
    newHistory.currentIndex++;
    commit('setHistory', newHistory);

    return content;
  }
};
